import { useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import type { Control, UseFormSetValue } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import {
  Button,
  Chip,
  ComboBox,
  FieldError,
  Form,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Spinner,
} from '@heroui/react'

import { FormSection } from '@/components/ui/form-section'
import { NumberFieldControl, SelectFieldControl } from '@/components/ui/form-fields'
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@/components/ui/icons'
import { errorMessage, toApiError } from '@/lib/api/api-error'

import {
  inventoryKey,
  useRecipeActions,
  useStockItems,
  useStockItemsById,
  useUnitCatalog,
} from '../hooks'
import type { UnitCatalog } from '../hooks'
import { RecipeStatus, StockItemStatus, StockItemType } from '../types'
import type { Recipe, RecipeLineInput, StockItem } from '../types'
import {
  lineCostOf,
  recipeCost,
  recipeQuantity,
  recipeStatusLabels,
  unitCost,
  unitsLike,
} from './inventory-format'
import type { LineCost } from './inventory-format'
import { diffRecipe, snapshotOf } from './recipe-changes'
import type { RecipeSnapshot } from './recipe-changes'

/** Recipe quantities take a fourth decimal the stock figures do not: a pinch is 0.0005 kg. */
const LINE_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 4 }

/**
 * The smallest quantity a line can hold. The service stores four decimals and
 * refuses zero, so this is the floor. The schema holds it rather than the
 * field's `minValue`: the field's −/+ step whole units counted from its
 * minimum, and from 0.0001 they would land on 1.0001, 2.0001.
 */
const MIN_LINE_QTY = 0.0001

/** How many ingredients the picker offers at once. Past these, typing narrows them. */
const INGREDIENT_MATCH_LIMIT = 10

/** How long the merchant has to stop typing before inventory is asked again. */
const SEARCH_DEBOUNCE_MS = 300

const lineSchema = z.object({
  stockItemId: z.string().min(1, 'Choose an ingredient.'),
  qty: z
    .number({ required_error: 'Enter a quantity.', invalid_type_error: 'Enter a quantity.' })
    .min(MIN_LINE_QTY, `At least ${MIN_LINE_QTY}.`),
  unitOfMeasure: z.string().min(1, 'Choose a unit.'),
})

/**
 * Only the ingredients are edited here. The recipe's name and yield are not on
 * the page: a draft arrives with both set (the item's name, one of its unit),
 * and a new recipe is given the same.
 */
const schema = z.object({
  lines: z
    .array(lineSchema)
    .min(1, 'Add at least one ingredient.')
    .refine(
      (lines) => new Set(lines.map((line) => line.stockItemId)).size === lines.length,
      'Each ingredient can only be listed once.',
    ),
})

type FormValues = z.infer<typeof schema>

/** A blank ingredient line, ready to fill in. */
const EMPTY_LINE = { stockItemId: '', qty: Number.NaN, unitOfMeasure: '' }

/** The form's values for a snapshot — one blank line when it has none, so there is somewhere to start. */
function formValuesOf(snapshot: RecipeSnapshot): FormValues {
  return {
    lines: snapshot.lines.length
      ? snapshot.lines.map((line) => ({ ...line, qty: line.qty ?? Number.NaN }))
      : [EMPTY_LINE],
  }
}

interface RecipeEditorProps {
  /** The COMPOSITE item the recipe makes. */
  item: StockItem
  /**
   * The recipe as last read from the server — `null` for one not created yet.
   * It can move on while the editor is open (a save elsewhere, a modifier added);
   * the editor keeps editing the version it opened and offers the newer one.
   */
  recipe: Recipe | null
  /** Set when the page was reached straight from creating this recipe. */
  justCreated?: boolean
  /** A new recipe was saved; the page moves to its own address. */
  onCreated: (recipe: Recipe) => void
  /** Back to the list. Only called once unsaved changes are kept or let go. */
  onLeave: () => void
}

/**
 * One recipe's ingredients, edited on a page of its own.
 *
 * Three ways in, one editor. A DRAFT — the empty recipe every COMPOSITE item is
 * created with — is filled in and saved ACTIVE. An ACTIVE or archived recipe is
 * edited and keeps its status. An item with no recipe at all gets one created.
 *
 * **Versions.** The service numbers each saved state of a recipe and refuses a
 * save made against an old number. The editor shows the version beside the
 * name and sends it with each save. When a newer version turns up — saved in
 * another tab, or by adding a modifier — it is loaded at once if nothing here
 * is unsaved, and offered otherwise rather than either side being silently
 * overwritten.
 */
export function RecipeEditor({ item, recipe, justCreated, onCreated, onLeave }: RecipeEditorProps) {
  const { save } = useRecipeActions()

  /** The version this editor is editing — what the changes are measured against and what a save is sent with. */
  const [base, setBase] = useState<Recipe | null>(recipe)
  /** The last save made here, for the line that confirms it. */
  const [savedNote, setSavedNote] = useState<'created' | 'saved' | null>(
    justCreated && recipe ? 'created' : null,
  )
  const [isConfirmingLeave, setIsConfirmingLeave] = useState(false)

  /* One idempotency key per distinct body, as on the item form: a retry of the
     same recipe after a timeout replays the first answer instead of failing as
     "already has a recipe". Only a create carries one. */
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null)

  const savedSnapshot = useMemo(() => snapshotOf(base, item), [base, item])
  const savedValues = useMemo(() => formValuesOf(savedSnapshot), [savedSnapshot])

  /* `values` rather than `defaultValues`: the form follows the version being
     edited, so moving `base` on — after a save, or to a newer version — resets
     the fields to it without a reset call of our own. */
  const { control, handleSubmit, setValue, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    values: savedValues,
  })

  const lines = useFieldArray({ control, name: 'lines' })
  const values = useWatch({ control }) as FormValues

  const isCreating = !base
  const isFillingDraft = base?.status === RecipeStatus.Draft
  const isArchivedItem = item.status === StockItemStatus.Archived

  /* Every ingredient the saved recipe or the form names, read by id: each
     line's picker searches inventory on its own, so a chosen ingredient is not
     necessarily among its matches, and the costs and the changes list need it. */
  const namedIds = useMemo(
    () => [
      ...new Set(
        [...savedSnapshot.lines, ...(values.lines ?? [])]
          .map((line) => line?.stockItemId)
          .filter((id): id is string => Boolean(id)),
      ),
    ],
    [savedSnapshot.lines, values.lines],
  )
  const named = useStockItemsById(namedIds)

  const names = useMemo(
    () => new Map([...named.values()].map((ingredient) => [ingredient.id, ingredient.name])),
    [named],
  )

  /** The active units — which a line may use for its ingredient, and how it converts. */
  const { catalog: units } = useUnitCatalog()

  /* What each line costs, in the form's order, at its ingredient's unit cost —
     converted, since a line can be in grams of something costed per kilo. `null`
     while the line has no ingredient or no quantity yet. */
  const lineCosts = useMemo(
    () =>
      (values.lines ?? []).map((line) =>
        lineCostOf(
          line?.qty,
          line?.unitOfMeasure,
          line?.stockItemId ? named.get(line.stockItemId) : undefined,
          units,
        ),
      ),
    [named, values.lines, units],
  )

  const totalCost = lineCosts.reduce((sum, line) => sum + (line?.cost ?? 0), 0)
  /* Lines with an ingredient and a quantity but no cost worked out: their unit
     has to be converted, and the units to do it with have not loaded yet — or
     it cannot be converted at all. Either way the total leaves them out. */
  const unpricedCount = (values.lines ?? []).filter(
    (line, index) =>
      Boolean(line?.stockItemId) && Number.isFinite(line?.qty) && lineCosts[index] === null,
  ).length
  /** While the units load, the total is not ready yet rather than too low. */
  const isCostPending = unpricedCount > 0 && units.size === 0
  /** Lines the total leaves out: an ingredient with no cost yet, or a unit that does not convert. */
  const uncostedCount =
    lineCosts.filter((line) => line?.perUnit === 0).length + (isCostPending ? 0 : unpricedCount)
  const yieldQty = savedSnapshot.yieldQty || 1

  const changes = useMemo(
    () => diffRecipe(savedSnapshot, { ...savedSnapshot, lines: values.lines ?? [] }, names),
    [savedSnapshot, values, names],
  )
  const hasChanges = changes.length > 0

  /** The version the server holds, when it has moved past the one being edited. */
  const newer = recipe && base && recipe.version > base.version ? recipe : null

  function adopt(next: Recipe) {
    setBase(next)
    setSavedNote(null)
    save.reset()
  }

  // Nothing here would be lost, so the newer version simply takes over. Set
  // while rendering, as React suggests for state that follows a prop; `newer`
  // is null once it has, so this runs once per version.
  if (newer && !hasChanges) setBase(newer)

  // Closing the tab or reloading would drop the edits without a word.
  useEffect(() => {
    if (!hasChanges) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasChanges])

  const submit = handleSubmit(async (formValues) => {
    const recipeLines: RecipeLineInput[] = formValues.lines.map((line) => ({
      stockItemId: line.stockItemId,
      qty: line.qty,
      unitOfMeasure: line.unitOfMeasure,
    }))

    if (!base) {
      // The defaults a draft would have had: the item's name, one of its unit.
      const body = {
        stockItemId: item.id,
        name: savedSnapshot.name,
        yieldQty: 1,
        yieldUnit: savedSnapshot.yieldUnit,
        lines: recipeLines,
      }
      const serialised = JSON.stringify(body)
      const next =
        attempt?.body === serialised ? attempt : { key: crypto.randomUUID(), body: serialised }
      setAttempt(next)

      const created = await save.mutateAsync({ kind: 'create', body, idempotencyKey: next.key })
      onCreated(created)
      return
    }

    const saved = await save.mutateAsync({
      kind: 'update',
      id: base.id,
      // Name and yield are left off, so they stay as they are.
      body: {
        version: base.version,
        lines: recipeLines,
        // A draft becomes a recipe in use by being filled in. An active or
        // archived one keeps whatever it was.
        ...(isFillingDraft && { status: RecipeStatus.Active }),
      },
    })

    setSavedNote('saved')
    setBase(saved)
  })

  function leave() {
    if (hasChanges) setIsConfirmingLeave(true)
    else onLeave()
  }

  function discard() {
    reset(savedValues)
    save.reset()
  }

  const isConflict = save.error != null && toApiError(save.error).status === 409

  return (
    <Form
      onSubmit={submit}
      validationBehavior="aria"
      className="flex flex-col gap-4"
      aria-label={`Recipe for ${item.name}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          size="sm"
          onPress={leave}
          className="text-muted hover:text-foreground -ms-3 h-9 rounded-full px-3"
        >
          <ArrowLeftIcon width={16} height={16} />
          Recipes
        </Button>
      </div>

      {isConfirmingLeave && (
        <div
          role="alert"
          className="border-warning/40 bg-warning/5 flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 text-sm"
        >
          <span className="flex-1">You have unsaved changes. Leave without saving them?</span>
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-9 rounded-full border px-4"
            onPress={() => setIsConfirmingLeave(false)}
          >
            Keep editing
          </Button>
          <Button variant="danger" size="sm" className="h-9 rounded-full px-4" onPress={onLeave}>
            Discard and leave
          </Button>
        </div>
      )}

      <header className="border-border bg-surface rounded-2xl border p-4 sm:p-5">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-semibold tracking-tight">
              {base?.name ?? `New recipe for ${item.name}`}
            </h2>
            {base && (
              <Chip size="sm" variant="soft" aria-label={`Version ${base.version}`}>
                v{base.version}
              </Chip>
            )}
            {base && (
              <Chip
                size="sm"
                variant="soft"
                color={
                  base.status === RecipeStatus.Active
                    ? 'success'
                    : base.status === RecipeStatus.Draft
                      ? 'warning'
                      : 'default'
                }
              >
                {recipeStatusLabels[base.status]}
              </Chip>
            )}
          </span>
          <p className="text-muted text-xs">
            Makes {recipeQuantity(savedSnapshot.yieldQty ?? 1)} {savedSnapshot.yieldUnit} of{' '}
            <span className="text-foreground font-medium">{item.name}</span> · {item.sku}
          </p>
        </div>
      </header>

      {isArchivedItem && (
        <p className="border-border text-muted rounded-2xl border border-dashed px-4 py-3 text-sm">
          {item.name} is archived, so its recipe cannot be changed.
        </p>
      )}

      {newer && hasChanges && (
        <div
          role="alert"
          className="border-warning/40 bg-warning/5 flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 text-sm"
        >
          <span className="flex-1">
            This recipe was changed somewhere else while you were editing. Load the latest to keep
            going — your unsaved changes here will be dropped.
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-9 rounded-full border px-4"
            onPress={() => adopt(newer)}
          >
            Load latest
          </Button>
        </div>
      )}

      {savedNote && !hasChanges && (
        <p
          role="status"
          className="border-success/40 bg-success/5 text-success rounded-2xl border px-4 py-3 text-sm"
        >
          {savedNote === 'created' ? 'Recipe created.' : 'Recipe saved.'}
        </p>
      )}

      <FormSection
        title={`Ingredients (${lines.fields.length})`}
        description="Each one is another stock item. Its unit can be any that measures the same thing — grams of something stocked in kilos. Saving replaces the list as shown, in this order."
        action={
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-8 rounded-full border px-3 text-xs"
            onPress={() => lines.append(EMPTY_LINE)}
          >
            <PlusIcon width={14} height={14} />
            Add ingredient
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {lines.fields.map((field, index) => (
            <LineRow
              key={field.id}
              index={index}
              control={control}
              setValue={setValue}
              recipeItemId={item.id}
              selected={
                values.lines?.[index]?.stockItemId
                  ? named.get(values.lines[index].stockItemId)
                  : undefined
              }
              units={units}
              takenIds={(values.lines ?? [])
                .filter((_, other) => other !== index)
                .map((line) => line?.stockItemId)
                .filter((id): id is string => Boolean(id))}
              cost={lineCosts[index] ?? null}
              canRemove={lines.fields.length > 1}
              onRemove={() => lines.remove(index)}
            />
          ))}

          <div className="border-border flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-t pt-3">
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">Recipe cost</span>
              <span className="text-muted text-xs">
                {isCostPending
                  ? 'Working out the cost…'
                  : uncostedCount > 0
                    ? `${uncostedCount} ${uncostedCount === 1 ? 'ingredient has' : 'ingredients have'} no cost yet, so this is lower than the real cost.`
                    : `To make ${recipeQuantity(yieldQty)} ${savedSnapshot.yieldUnit} of ${item.name}.`}
              </span>
            </span>
            <span className="flex flex-col items-end gap-0.5">
              <span className="text-lg font-semibold tabular-nums">
                {isCostPending ? '…' : recipeCost(totalCost)}
              </span>
              {yieldQty !== 1 && !isCostPending && (
                <span className="text-muted text-xs tabular-nums">
                  {recipeCost(totalCost / yieldQty)} / {savedSnapshot.yieldUnit}
                </span>
              )}
            </span>
          </div>

          {formState.errors.lines?.root?.message && (
            <p role="alert" className="text-danger text-xs">
              {formState.errors.lines.root.message}
            </p>
          )}
          {formState.errors.lines?.message && (
            <p role="alert" className="text-danger text-xs">
              {formState.errors.lines.message}
            </p>
          )}
        </div>
      </FormSection>

      {save.error != null && (
        <p role="alert" className="text-danger text-sm">
          {isConflict
            ? 'This recipe was changed somewhere else, so your save did not go through. Load the latest above, then make your changes again.'
            : errorMessage(save.error, 'Could not save this recipe.')}
        </p>
      )}

      {!isArchivedItem && (
        <div className="border-border bg-surface sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-end gap-3 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:-mx-6 sm:px-6">
          <span className="text-muted me-auto text-xs">
            {hasChanges ? 'You have unsaved changes.' : 'All changes saved.'}
          </span>
          {hasChanges && !isCreating && (
            <Button
              variant="ghost"
              onPress={discard}
              isDisabled={save.isPending}
              className="h-10 rounded-full px-4"
            >
              Discard changes
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            isDisabled={save.isPending || !hasChanges || Boolean(newer)}
            className="h-10 rounded-full px-5 font-medium"
          >
            {save.isPending && <Spinner size="sm" />}
            {save.isPending ? 'Saving…' : 'Save recipe'}
          </Button>
        </div>
      )}
    </Form>
  )
}

interface LineRowProps {
  index: number
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
  /** The item the recipe makes. Never offered: a recipe cannot use its own item. */
  recipeItemId: string
  /** The ingredient this line has, once read — kept in the list whatever the search finds. */
  selected: StockItem | undefined
  /** The active units, for the ones this line's ingredient can be measured in. */
  units: UnitCatalog
  /** The ingredients other lines already use — offered, but not choosable twice. */
  takenIds: string[]
  /** What the line costs. `null` until it has an ingredient and a quantity. */
  cost: LineCost | null
  canRemove: boolean
  onRemove: () => void
}

/**
 * One ingredient: which item, how much, in what — and what that much costs.
 *
 * The item is searched for rather than scrolled to: the picker offers the first
 * few INGREDIENT items, and typing asks inventory for the ones whose name or
 * SKU match. Each line searches on its own, so typing in one leaves the others'
 * lists alone; lines with the same search share one request.
 *
 * Choosing the item resets the unit to the item's own — the one unit always
 * certain to pass — and narrows the unit list to the ones that share its base,
 * so the form cannot offer a line the API would refuse.
 */
function LineRow({
  index,
  control,
  setValue,
  recipeItemId,
  selected,
  units,
  takenIds,
  cost,
  canRemove,
  onRemove,
}: LineRowProps) {
  const queryClient = useQueryClient()
  const ingredientUnit = selected?.unitOfMeasure

  const [search, setSearch] = useState('')
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  /* Picking an item writes its name into the box, which reads as a search for
     it. That is not something the merchant typed, so it lists the first few
     again — or reopening the list would offer only the item already chosen. */
  const effectiveSearch = searchText === selected?.name ? '' : searchText

  const query = useMemo(
    () => ({
      limit: INGREDIENT_MATCH_LIMIT,
      offset: 0,
      category: StockItemType.Ingredient,
      // Left off when empty: the service answers `search=` with a 400.
      ...(effectiveSearch ? { search: effectiveSearch } : {}),
    }),
    [effectiveSearch],
  )
  const { items: matches, isLoading } = useStockItems(query)

  const options = useMemo(() => {
    const found = matches.filter((match) => match.id !== recipeItemId)
    return selected && !found.some((match) => match.id === selected.id)
      ? [selected, ...found]
      : found
  }, [matches, selected, recipeItemId])

  return (
    /* The ingredient takes what is left; the rest are sized to what they hold —
       a four-decimal quantity between its −/+, a unit symbol, a cost. `minmax(0, …)` lets a long
       ingredient name truncate instead of pushing the row wider. */
    <div className="border-border grid gap-3 rounded-xl border p-3 sm:grid-cols-[minmax(0,1fr)_10rem_5.5rem_7.5rem_2.5rem]">
      <Controller
        control={control}
        name={`lines.${index}.stockItemId`}
        render={({ field, fieldState }) => (
          <ComboBox
            value={field.value || null}
            onChange={(key) => {
              const picked = options.find((option) => option.id === String(key))
              field.onChange(picked?.id ?? '')
              if (picked) {
                // Seeded so the line's cost and unit list need not wait for
                // the parent to read the item back by id.
                queryClient.setQueryData([...inventoryKey, 'item', picked.id], picked)
                setValue(`lines.${index}.unitOfMeasure`, picked.unitOfMeasure, {
                  shouldValidate: true,
                })
              }
            }}
            onInputChange={setSearch}
            // Inventory has already filtered by name and SKU; filtering its
            // answer again by the box's text would drop the SKU matches.
            defaultFilter={() => true}
            allowsEmptyCollection
            onBlur={field.onBlur}
            isInvalid={fieldState.invalid}
            isRequired
            fullWidth
            className="flex flex-col gap-1.5"
          >
            <Label>Ingredient</Label>
            <ComboBox.InputGroup>
              <Input ref={field.ref} placeholder="Search ingredients…" />
              <ComboBox.Trigger />
            </ComboBox.InputGroup>
            <ComboBox.Popover>
              <ListBox
                disabledKeys={takenIds}
                renderEmptyState={() => (
                  <p className="text-muted px-3 py-2 text-xs">
                    {isLoading
                      ? 'Searching…'
                      : effectiveSearch
                        ? `No ingredient matches “${effectiveSearch}”.`
                        : 'No ingredients yet. Add one on the Stock items tab.'}
                  </p>
                )}
              >
                {options.map((option) => (
                  // The name alone, since it is what the box shows once chosen.
                  <ListBoxItem key={option.id} id={option.id} textValue={option.name}>
                    <span className="flex flex-col">
                      <span>{option.name}</span>
                      <span className="text-muted text-xs">
                        {option.sku} · {option.unitOfMeasure}
                      </span>
                    </span>
                  </ListBoxItem>
                ))}
              </ListBox>
            </ComboBox.Popover>
            <FieldError>{fieldState.error?.message}</FieldError>
          </ComboBox>
        )}
      />

      <NumberFieldControl
        control={control}
        name={`lines.${index}.qty`}
        label="Quantity"
        // 0 rather than MIN_LINE_QTY, so −/+ step through 0, 1, 2 — see there.
        minValue={0}
        formatOptions={LINE_FORMAT}
        isRequired
      />

      <SelectFieldControl
        control={control}
        name={`lines.${index}.unitOfMeasure`}
        label="Unit"
        isRequired
        isDisabled={!ingredientUnit}
        // The symbol alone — kg, g, L — as the quantity beside it reads: "250 g".
        options={(ingredientUnit ? unitsLike(ingredientUnit, units) : []).map((option) => ({
          value: option,
          label: option,
        }))}
      />

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium">Cost</span>
        <span className="flex min-h-10 flex-col justify-center tabular-nums sm:items-end">
          {cost ? (
            <>
              <span className={cost.perUnit === 0 ? 'text-muted' : 'font-medium'}>
                {recipeCost(cost.cost)}
              </span>
              <span className="text-muted text-xs">
                {cost.perUnit === 0 ? 'No cost set' : `${unitCost(cost.perUnit)} / ${cost.unit}`}
              </span>
            </>
          ) : (
            <span className="text-muted">—</span>
          )}
        </span>
      </div>

      <Button
        variant="ghost"
        aria-label="Remove ingredient"
        // A recipe with no ingredients cannot be saved, so the last row keeps a
        // disabled remove button rather than losing it.
        isDisabled={!canRemove}
        onPress={onRemove}
        className="text-muted hover:text-danger size-10 self-end rounded-full"
      >
        <TrashIcon width={16} height={16} />
      </Button>
    </div>
  )
}
